import './style.scss';

import * as XLSX from 'xlsx';
import xls from '../../assets/icons/xls.svg';
import { formatIsoDateInBogota } from '../../utils/dateTime';
import {
  filterAttendancesByAdminScope,
  normalizeAdminScope
} from '../../utils/adminScope';

const ATTENDANCE_HEADERS = [
  'COMUNA ID',
  'COMUNA',
  'BARRIO_ID',
  'BARRIO',
  'ID_GRUPO',
  'GRUPO',
  'TIPO_RECURSO',
  'TIPO_ESCUELA',
  'ID REGISTRO ASISTENCIA',
  'ID ENCUENTRO',
  'NOMBRE ENCUENTRO',
  'FECHA ASISTENCIA',
  'BENEFICIARIO TIPO DOC',
  'BENEFICIARIO DOCUMENTO',
  'BENEFICIARIO',
  'ASISTIO',
  'ARGUMENTO',
  'OBSERVACIONES'
];

const HEADER_CELL_STYLE = {
  font: {
    bold: true,
    color: { rgb: '1F2A44' }
  },
  fill: {
    patternType: 'solid',
    fgColor: { rgb: 'B7CCE3' }
  },
  alignment: {
    horizontal: 'center',
    vertical: 'center'
  }
};

const HIGHLIGHT_CELL_STYLE = {
  fill: {
    patternType: 'solid',
    fgColor: { rgb: 'E9F1DB' }
  },
  alignment: {
    vertical: 'center'
  }
};

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeMeetingType(value) {
  return String(value ?? '').trim().toUpperCase();
}

function buildActivityKey(type, order) {
  return `${normalizeMeetingType(type)}_${order}`;
}

function getMeetingGroupId(record) {
  return toNumber(
    record?.meeting_group_id ?? record?.meetingGroupId ?? record?.meetingGroup?.id
  );
}

function getGroupId(record) {
  return toNumber(record?.groupId ?? record?.group_id ?? record?.group?.id);
}

function formatDateValue(value) {
  const formatted = formatIsoDateInBogota(value);
  if (formatted) return formatted;

  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const [datePart] = raw.split('T');
  return datePart || raw;
}

function normalizeCellValue(value) {
  return String(value ?? '').trim();
}

function getActivityLabel(type, order) {
  return normalizeMeetingType(type) === 'EVENT'
    ? `Evento ${order}`
    : `Encuentro ${order}`;
}

function hasRestrictedSchoolOrGroupName(group, school, scope = '') {
  const normalizedScope = normalizeAdminScope(scope);

  if (normalizedScope) {
    return false;
  }

  const values = [
    group?.code,
    group?.name,
    school?.code,
    school?.name
  ];

  return values.some(value => {
    const normalized = String(value ?? '').trim().toUpperCase();
    return normalized.includes('NDN') || normalized.includes('GD');
  });
}

function buildAttendanceSheetRows(attendancesModel, meetingGroups, groups, scope = '') {
  const meetingGroupById = new Map(
    (Array.isArray(meetingGroups) ? meetingGroups : [])
      .map(meetingGroup => [toNumber(meetingGroup?.id), meetingGroup])
      .filter(([meetingGroupId]) => Boolean(meetingGroupId))
  );
  const groupById = new Map(
    (Array.isArray(groups) ? groups : [])
      .map(group => [toNumber(group?.id), group])
      .filter(([groupId]) => Boolean(groupId))
  );

  return (Array.isArray(attendancesModel) ? attendancesModel : [])
    .map(attendance => {
      const meetingGroupId = getMeetingGroupId(attendance);
      const meeting =
        meetingGroupById.get(meetingGroupId) ?? attendance?.meetingGroup ?? null;
      const groupId = getGroupId(meeting);
      const group = groupById.get(groupId) ?? meeting?.group ?? null;
      const school = group?.school ?? meeting?.school ?? null;
      if (hasRestrictedSchoolOrGroupName(group, school, scope)) {
        return null;
      }
      const municipality = group?.municipality ?? school?.municipality ?? null;
      const neighborhood = group?.neighborhood ?? school?.neighborhood ?? null;
      const meetingType = normalizeMeetingType(meeting?.type);
      const meetingOrder = toNumber(meeting?.order);
      const beneficiary = attendance?.beneficiary ?? {};

      return {
        'COMUNA ID':
          municipality?.id ??
          group?.municipalityId ??
          group?.municipality_id ??
          school?.municipalityId ??
          school?.municipality_id ??
          '',
        COMUNA: municipality?.name ?? '',
        BARRIO_ID:
          neighborhood?.id ??
          group?.neighborhoodId ??
          group?.neighborhood_id ??
          school?.neighborhoodId ??
          school?.neighborhood_id ??
          '',
        BARRIO: neighborhood?.name ?? '',
        ID_GRUPO: group?.id ?? '',
        GRUPO: group?.code ?? '',
        TIPO_RECURSO:
          school?.resourceTypeId ?? school?.resource_type_id ?? '',
        TIPO_ESCUELA: school?.type ?? '',
        'ID REGISTRO ASISTENCIA': attendance?.id ?? '',
        'ID ENCUENTRO': meetingGroupId ?? '',
        'NOMBRE ENCUENTRO':
          meetingType && meetingOrder
            ? getActivityLabel(meetingType, meetingOrder)
            : '',
        'FECHA ASISTENCIA': formatDateValue(
          attendance?.attendance_date ?? attendance?.attendanceDate
        ),
        'BENEFICIARIO TIPO DOC':
          beneficiary?.documentType ?? beneficiary?.document_type ?? '',
        'BENEFICIARIO DOCUMENTO': beneficiary?.document ?? '',
        BENEFICIARIO: beneficiary?.fullName ?? beneficiary?.full_name ?? '',
        ASISTIO: attendance?.attended ? 'SI' : 'NO',
        ARGUMENTO: attendance?.argument ?? '',
        OBSERVACIONES: attendance?.notes ?? ''
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const leftMeetingId = toNumber(left['ID ENCUENTRO']) ?? 0;
      const rightMeetingId = toNumber(right['ID ENCUENTRO']) ?? 0;

      if (leftMeetingId !== rightMeetingId) {
        return leftMeetingId - rightMeetingId;
      }

      const leftAttendanceId = toNumber(left['ID REGISTRO ASISTENCIA']) ?? 0;
      const rightAttendanceId = toNumber(right['ID REGISTRO ASISTENCIA']) ?? 0;
      return leftAttendanceId - rightAttendanceId;
    });
}

function buildActivityCatalog(meetingGroups) {
  const maxMeetingOrder = meetingGroups.reduce((maxOrder, meetingGroup) => {
    const type = normalizeMeetingType(meetingGroup?.type);
    const order = toNumber(meetingGroup?.order);

    if (type !== 'MEETING' || !Number.isInteger(order) || order <= 0) {
      return maxOrder;
    }

    return Math.max(maxOrder, order);
  }, 0);

  return Array.from({ length: maxMeetingOrder }, (_, index) => {
    const order = index + 1;
    const type = 'MEETING';

    return {
      key: buildActivityKey(type, order),
      type,
      order,
      label: getActivityLabel(type, order)
    };
  });
}

function buildGroupActivitiesMap(meetingGroups) {
  const groupActivitiesMap = new Map();

  meetingGroups.forEach(meetingGroup => {
    const groupId = getGroupId(meetingGroup);
    const type = normalizeMeetingType(meetingGroup?.type);
    const order = toNumber(meetingGroup?.order);
    const meetingGroupId = toNumber(meetingGroup?.id);

    if (
      !groupId ||
      !meetingGroupId ||
      type !== 'MEETING' ||
      !Number.isInteger(order) ||
      order <= 0
    ) {
      return;
    }

    const activityKey = buildActivityKey(type, order);
    const activities = groupActivitiesMap.get(groupId) ?? new Map();
    const current = activities.get(activityKey);
    const currentId = toNumber(current?.id) ?? 0;

    if (!current || meetingGroupId > currentId) {
      activities.set(activityKey, meetingGroup);
    }

    groupActivitiesMap.set(groupId, activities);
  });

  return groupActivitiesMap;
}

function buildAttendanceMetrics(attendancesModel) {
  const attendanceCountByMeetingGroup = new Map();

  attendancesModel.forEach(attendance => {
    const meetingGroupId = getMeetingGroupId(attendance);
    if (!meetingGroupId) return;

    const attended = Boolean(attendance?.attended);
    if (attended) {
      attendanceCountByMeetingGroup.set(
        meetingGroupId,
        (attendanceCountByMeetingGroup.get(meetingGroupId) ?? 0) + 1
      );
    }
  });

  return {
    attendanceCountByMeetingGroup
  };
}

function buildSummaryRows({
  meetingGroups,
  groups,
  attendancesModel,
  scope = ''
}) {
  const normalizedMeetingGroups = Array.isArray(meetingGroups) ? meetingGroups : [];
  const normalizedGroups = Array.isArray(groups) ? groups : [];
  const normalizedAttendances = Array.isArray(attendancesModel)
    ? attendancesModel
    : [];
  const groupById = new Map(
    normalizedGroups
      .map(group => [toNumber(group?.id), group])
      .filter(([groupId]) => Boolean(groupId))
  );
  const filteredMeetingGroups = normalizedMeetingGroups.filter(meetingGroup => {
    const group =
      groupById.get(getGroupId(meetingGroup)) ?? meetingGroup?.group ?? null;
    const school = group?.school ?? meetingGroup?.school ?? null;
    return !hasRestrictedSchoolOrGroupName(group, school, scope);
  });
  const activityCatalog = buildActivityCatalog(filteredMeetingGroups);
  const groupActivitiesMap = buildGroupActivitiesMap(filteredMeetingGroups);

  const { attendanceCountByMeetingGroup } = buildAttendanceMetrics(
    normalizedAttendances
  );

  const rows = [];

  groupActivitiesMap.forEach((activitiesMap, groupId) => {
    const group = groupById.get(groupId);
    const school = group?.school ?? null;

    if (
      !group ||
      !school ||
      hasRestrictedSchoolOrGroupName(group, school, scope)
    ) {
      return;
    }

    const activityValues = {};
    let totalActivities = 0;
    let totalAttendance = 0;

    activityCatalog.forEach(activity => {
      const meetingGroup = activitiesMap.get(activity.key);
      if (!meetingGroup) {
        activityValues[activity.label] = '';
        return;
      }

      totalActivities += 1;
      const count =
        attendanceCountByMeetingGroup.get(toNumber(meetingGroup?.id)) ?? 0;
      totalAttendance += count;
      activityValues[activity.label] = count;
    });

    if (totalActivities === 0) {
      return;
    }

    rows.push({
      schoolCode: String(school?.code ?? '').trim(),
      groupCode: String(group?.code ?? '').trim(),
      totalActivities,
      totalAttendance,
      averageAttendance:
        totalActivities > 0 ? totalAttendance / totalActivities : 0,
      activityValues
    });
  });

  rows.sort((left, right) => {
    const schoolCompare = left.schoolCode.localeCompare(right.schoolCode, 'es', {
      numeric: true,
      sensitivity: 'base'
    });
    if (schoolCompare !== 0) return schoolCompare;

    return left.groupCode.localeCompare(right.groupCode, 'es', {
      numeric: true,
      sensitivity: 'base'
    });
  });

  const headers = [
    'ESCUELA',
    'GRUPO',
    ...activityCatalog.map(activity => activity.label),
    'PROMEDIO ASISTENCIA'
  ];

  const sheetRows = rows.map(row => ({
    ESCUELA: row.schoolCode,
    GRUPO: row.groupCode,
    ...row.activityValues,
    'PROMEDIO ASISTENCIA': Math.round(row.averageAttendance)
  }));

  return { headers, rows: sheetRows };
}

function buildVerticalMerges(rows, headers, mergeConfigs) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  return mergeConfigs.reduce((merges, mergeConfig) => {
    const columnIndex = headers.indexOf(mergeConfig.key);
    if (columnIndex < 0) return merges;

    let startIndex = 0;

    for (let index = 1; index <= rows.length; index += 1) {
      const previousRow = rows[index - 1];
      const currentRow = rows[index];
      const previousValue = normalizeCellValue(previousRow?.[mergeConfig.key]);
      const currentValue = normalizeCellValue(currentRow?.[mergeConfig.key]);
      const isSameValue =
        index < rows.length &&
        previousValue !== '' &&
        previousValue === currentValue;
      const isSameGroup = (mergeConfig.within ?? []).every(
        groupKey =>
          normalizeCellValue(previousRow?.[groupKey]) ===
          normalizeCellValue(currentRow?.[groupKey])
      );

      if (isSameValue && isSameGroup) {
        continue;
      }

      if (previousValue !== '' && index - startIndex > 1) {
        merges.push({
          s: { r: startIndex + 1, c: columnIndex },
          e: { r: index, c: columnIndex }
        });
      }

      startIndex = index;
    }

    return merges;
  }, []);
}

function createSheet(rows, headers, widths, options = {}) {
  const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
  worksheet['!cols'] = widths;

  if (Array.isArray(options.merges) && options.merges.length > 0) {
    worksheet['!merges'] = options.merges;
  }

  headers.forEach((header, columnIndex) => {
    const headerCellRef = XLSX.utils.encode_cell({ r: 0, c: columnIndex });
    if (worksheet[headerCellRef]) {
      worksheet[headerCellRef].s = HEADER_CELL_STYLE;
    }
  });

  if (Array.isArray(options.highlightColumns) && options.highlightColumns.length > 0) {
    const highlightedColumns = new Set(options.highlightColumns);

    headers.forEach((header, columnIndex) => {
      if (!highlightedColumns.has(header)) {
        return;
      }

      for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex + 1, c: columnIndex });
        if (!worksheet[cellRef]) {
          continue;
        }

        worksheet[cellRef].s = HIGHLIGHT_CELL_STYLE;
      }
    });
  }

  return worksheet;
}

function downloadWorkbook(workbook, fileName) {
  const workbookOutput = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    cellStyles: true
  });
  const blob = new Blob([workbookOutput], {
    type: 'application/octet-stream'
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function resolveAttendancesData(attendances) {
  const meetingGroups = Array.isArray(attendances?.meetingGroupsTable)
    ? attendances.meetingGroupsTable
    : Array.isArray(attendances?.meetings)
      ? attendances.meetings
      : [];
  const groups = Array.isArray(attendances?.groups) ? attendances.groups : [];
  const attendanceModels = Array.isArray(attendances?.attendancesModel)
    ? attendances.attendancesModel
    : [];
  const hasData = meetingGroups.length > 0 || attendanceModels.length > 0;

  return {
    meetingGroups,
    groups,
    attendanceModels,
    hasData
  };
}

function buildAndDownloadReport(attendances, scope = '') {
  const scopedAttendances = filterAttendancesByAdminScope(attendances, scope);
  const { meetingGroups, groups, attendanceModels, hasData } =
    resolveAttendancesData(scopedAttendances);
  if (!hasData) return false;

  const workbook = XLSX.utils.book_new();
  const attendanceRows = buildAttendanceSheetRows(
    attendanceModels,
    meetingGroups,
    groups,
    scope
  );
  const schoolsSheet = buildSummaryRows({
    meetingGroups,
    groups,
    attendancesModel: attendanceModels,
    scope
  });

  const rawSheet = createSheet(attendanceRows, ATTENDANCE_HEADERS, [
    { wch: 12 },
    { wch: 18 },
    { wch: 12 },
    { wch: 20 },
    { wch: 12 },
    { wch: 18 },
    { wch: 14 },
    { wch: 16 },
    { wch: 20 },
    { wch: 14 },
    { wch: 22 },
    { wch: 16 },
    { wch: 22 },
    { wch: 22 },
    { wch: 30 },
    { wch: 10 },
    { wch: 20 },
    { wch: 30 }
  ]);

  const summaryWidths = [
    { wch: 16 },
    { wch: 20 },
    ...schoolsSheet.headers.slice(2, -1).map(() => ({ wch: 12 })),
    { wch: 20 }
  ];

  const schoolsWorksheet = createSheet(
    schoolsSheet.rows,
    schoolsSheet.headers,
    summaryWidths,
    {
      highlightColumns: [
        'ESCUELA',
        'GRUPO',
        'PROMEDIO ASISTENCIA'
      ],
      merges: buildVerticalMerges(
        schoolsSheet.rows,
        schoolsSheet.headers,
        [
          { key: 'ESCUELA' }
        ]
      )
    }
  );

  XLSX.utils.book_append_sheet(workbook, rawSheet, 'BD ASISTENCIA');
  XLSX.utils.book_append_sheet(workbook, schoolsWorksheet, 'Escuelas formacion');

  downloadWorkbook(workbook, 'reporte_asistencias.xlsx');
  return true;
}

function DownloadAttendances({
  attendances,
  onRequestData,
  disabled = false,
  label = 'Descargar Reporte',
  title = 'Descargar reporte de asistencias',
  scope = ''
}) {
  const { hasData } = resolveAttendancesData(attendances);

  async function handleCreateXLS() {
    if (disabled) return;

    if (buildAndDownloadReport(attendances, scope)) {
      return;
    }

    if (typeof onRequestData !== 'function') {
      return;
    }

    const requestedAttendances = await onRequestData();
    buildAndDownloadReport(requestedAttendances, scope);
  }

  return (
    <div className={'download-container'}>
      <button
        className='download-button'
        onClick={handleCreateXLS}
        disabled={disabled || (!hasData && typeof onRequestData !== 'function')}
        title={title}>
        <span className='download-button__icon'>
          <img src={xls} alt='download' />
        </span>
        <span className='download-button__label'>{label}</span>
      </button>
    </div>
  );
}

export default DownloadAttendances;
